import React, { useState, useEffect } from "react";
import { getCurrentUser, resetPassword } from "../services/auth";
import "./UserProfile.css";

const UserProfile = ({ user: initialUser, onClose }) => {
  const [user, setUser] = useState(initialUser);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    setProfileLoading(true);
    getCurrentUser()
      .then((data) => {
        if (isMounted) {
          setUser(data.user);
        }
      })
      .catch(() => {
        if (isMounted) setError("Failed to load user info");
      })
      .finally(() => {
        if (isMounted) setProfileLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    // Clear messages when user starts typing
    if (message || error) {
      setMessage("");
      setError("");
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError("New password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      await resetPassword(passwordData.oldPassword, passwordData.newPassword);

      setMessage("Password updated successfully!");
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Auto close password reset form after success
      setTimeout(() => {
        setShowPasswordReset(false);
        setMessage("");
      }, 2000);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === "user-profile-overlay") {
      onClose();
    }
  };

  return (
    <div className="user-profile-overlay" onClick={handleOverlayClick}>
      <div className="user-profile-modal">
        <div className="user-profile-header">
          <h2>User Profile</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="user-profile-content">
          {profileLoading ? (
            <div style={{ textAlign: "center", padding: 32 }}>Loading...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              <div className="user-info-section">
                <div className="user-avatar">
                  <span className="avatar-icon">👤</span>
                </div>

                <div className="user-details">
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{user?.name}</span>
                  </div>

                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{user?.email}</span>
                  </div>

                  <div className="detail-item">
                    <label>Role:</label>
                    <span className={`role-badge ${user?.role || ""}`}>
                      {user?.role
                        ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                        : ""}
                    </span>
                  </div>
                </div>
              </div>

              <div className="password-section">
                {!showPasswordReset ? (
                  <button
                    className="reset-password-btn"
                    onClick={() => setShowPasswordReset(true)}
                  >
                    🔒 Reset Password
                  </button>
                ) : (
                  <div className="password-reset-form">
                    <h3>Reset Password</h3>

                    {error && <div className="error-message">{error}</div>}
                    {message && (
                      <div className="success-message">{message}</div>
                    )}

                    <form onSubmit={handlePasswordReset}>
                      <div className="form-group">
                        <label>Current Password:</label>
                        <input
                          type="password"
                          name="oldPassword"
                          value={passwordData.oldPassword}
                          onChange={handlePasswordChange}
                          required
                          placeholder="Enter current password"
                        />
                      </div>

                      <div className="form-group">
                        <label>New Password:</label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                          placeholder="Enter new password (min 6 characters)"
                        />
                      </div>

                      <div className="form-group">
                        <label>Confirm New Password:</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                          placeholder="Confirm new password"
                        />
                      </div>

                      <div className="form-actions">
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={() => {
                            setShowPasswordReset(false);
                            setPasswordData({
                              oldPassword: "",
                              newPassword: "",
                              confirmPassword: "",
                            });
                            setError("");
                            setMessage("");
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          type="submit"
                          className="submit-btn"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Password"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
